import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { accountingApi } from '@/api/accounting'
import { formatMoney } from '@/components/billing/formatMoney'
import { formatDateDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { EXPENSE_CATEGORIES, type Expense, type ExpenseCategory } from '@/types/accounting'

export function ExpensesTab() {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [expenses, setExpenses] = useState<Expense[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [category, setCategory] = useState<ExpenseCategory | ''>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuRow, setMenuRow] = useState<Expense | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formDate, setFormDate] = useState<Dayjs>(dayjs())
  const [formCategory, setFormCategory] = useState<ExpenseCategory>('other')
  const [formAmount, setFormAmount] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [saving, setSaving] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await accountingApi.listExpenses({
        page,
        per_page: perPage,
        ...(category ? { category } : {}),
      })
      setExpenses(result.data)
      setTotal(result.total)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [page, perPage, category, t])

  useEffect(() => {
    void load()
  }, [load])

  const openCreate = () => {
    setEditingId(null)
    setFormDate(dayjs())
    setFormCategory('other')
    setFormAmount('')
    setFormDescription('')
    setDialogOpen(true)
  }

  const openEdit = (expense: Expense) => {
    setEditingId(expense.id)
    setFormDate(dayjs(expense.date))
    setFormCategory(expense.category)
    setFormAmount(expense.amount)
    setFormDescription(expense.description ?? '')
    setDialogOpen(true)
    setMenuAnchor(null)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload = {
        date: formDate.format('YYYY-MM-DD'),
        category: formCategory,
        amount: Number(formAmount),
        description: formDescription || null,
      }
      if (editingId !== null) {
        await accountingApi.updateExpense(editingId, payload)
      } else {
        await accountingApi.createExpense(payload)
        setPage(1)
      }
      setDialogOpen(false)
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    setError(null)
    try {
      await accountingApi.deleteExpense(deleteTarget.id)
      setDeleteTarget(null)
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setDeleting(false)
    }
  }

  const amountInvalid = formAmount === '' || Number.isNaN(Number(formAmount)) || Number(formAmount) < 0

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('accounting.expenses.category', 'Категория')}
          value={category}
          onChange={(e) => {
            setCategory(e.target.value as ExpenseCategory | '')
            setPage(1)
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('accounting.expenses.allCategories', 'Все категории')}</MenuItem>
          {EXPENSE_CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>
              {t(`accounting.category.${c}`, c)}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={openCreate}>
          {t('accounting.expenses.add', 'Добавить расход')}
        </Button>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('accounting.expenses.date', 'Дата')}</TableCell>
              <TableCell>{t('accounting.expenses.category', 'Категория')}</TableCell>
              <TableCell align="right">{t('accounting.expenses.amount', 'Сумма')}</TableCell>
              <TableCell>{t('accounting.expenses.description', 'Описание')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}>
                    <Skeleton height={32} />
                  </TableCell>
                </TableRow>
              ))
            ) : expenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {t('accounting.expenses.empty', 'Расходов пока нет')}
                </TableCell>
              </TableRow>
            ) : (
              expenses.map((expense) => (
                <TableRow key={expense.id} hover>
                  <TableCell>{formatDateDMY(expense.date)}</TableCell>
                  <TableCell>{t(`accounting.category.${expense.category}`, expense.category)}</TableCell>
                  <TableCell align="right">{formatMoney(expense.amount, expense.currency)}</TableCell>
                  <TableCell>{expense.description || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget)
                        setMenuRow(expense)
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={Math.max(0, page - 1)}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        rowsPerPage={perPage}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10))
          setPage(1)
        }}
        rowsPerPageOptions={[10, 15, 25, 50]}
        labelRowsPerPage={t('accounting.rowsPerPage', 'Строк на странице:')}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('accounting.of', 'из')} ${count}`}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => menuRow && openEdit(menuRow)}>{t('common.edit', 'Изменить')}</MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteTarget(menuRow)
            setMenuAnchor(null)
          }}
          sx={{ color: 'error.main' }}
        >
          {t('common.delete', 'Удалить')}
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingId !== null ? t('accounting.expenses.editTitle', 'Изменить расход') : t('accounting.expenses.addTitle', 'Новый расход')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <DatePicker
              label={t('accounting.expenses.date', 'Дата')}
              value={formDate}
              onChange={(d) => d && setFormDate(d)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TextField
              select
              label={t('accounting.expenses.category', 'Категория')}
              value={formCategory}
              onChange={(e) => setFormCategory(e.target.value as ExpenseCategory)}
              fullWidth
            >
              {EXPENSE_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`accounting.category.${c}`, c)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('accounting.expenses.amount', 'Сумма')}
              type="number"
              value={formAmount}
              onChange={(e) => setFormAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label={t('accounting.expenses.description', 'Описание')}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || amountInvalid}>
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('accounting.expenses.deleteTitle', 'Удалить расход?')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('accounting.expenses.deleteConfirm', 'Расход будет удалён и перестанет учитываться в сводке.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {t('common.delete', 'Удалить')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}
